import React, { useEffect, useState, useRef } from 'react';
import '../styles/PhotoResult.css';
import { useParams } from 'react-router-dom';

// Fungsi hash sederhana untuk string (base64)
function simpleHash(str) {
  let hash = 0, i, chr;
  if (str.length === 0) return hash;
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
}

const API_BASE = 'https://api.laviopaperie.com';
function joinUrl(base, path) {
  if (!base.endsWith('/') && !path.startsWith('/')) return base + '/' + path;
  if (base.endsWith('/') && path.startsWith('/')) return base + path.slice(1);
  return base + path;
}

const PhotoGallery = () => {
  const { name } = useParams();
  const [slotCount, setSlotCount] = useState(null); // null until fetched
  const [images, setImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const resultContainerRef = useRef(null);
  const [selectedLayout, setSelectedLayout] = useState(1);
  const [selectedGrid, setSelectedGrid] = useState(1);
  const [gridPreviewUrl, setGridPreviewUrl] = useState(null);
  const [gifUrl, setGifUrl] = useState(null);
  const [gifLoading, setGifLoading] = useState(false);
  const [gifError, setGifError] = useState(null);
  const [manualGifIdx, setManualGifIdx] = useState(0);
  const manualGifInterval = 800;
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [showVideoPreview, setShowVideoPreview] = useState(false);

  // Fetch slotCount/grid from backend
  useEffect(() => {
    if (!name) return;
    fetch(joinUrl(API_BASE, `/api/photo-session/${name}`))
      .then(res => res.json())
      .then(data => {
        const baseSlotCount = data.grid || 2; // fallback ke 2 jika tidak ada
        setSlotCount(baseSlotCount + 2); // selalu tambah 2
        setSelectedLayout(data.layout || 1); // ambil layout dari backend
      })
      .catch(() => {
        setSlotCount(4); // 2 + 2 = 4
        setSelectedLayout(1);
      });
  }, [name]);

  // Generate images when slotCount is known
  useEffect(() => {
    if (!name || !slotCount) return;
    
    console.log('PhotoGallery: Generating images for', { name, slotCount });
    
    // Buat array untuk semua slot foto
    const slotPhotos = [];
    for (let i = 0; i < slotCount; i++) {
      const jpgUrl = joinUrl(API_BASE, `/uploads/${name}_${i+1}.jpg`);
      const pngUrl = joinUrl(API_BASE, `/uploads/${name}_${i+1}.png`);
      slotPhotos.push({ jpg: jpgUrl, png: pngUrl, idx: i });
      console.log(`PhotoGallery: Created slot ${i+1} with URLs:`, { jpg: jpgUrl, png: pngUrl });
    }
    
    const slotVideos = Array.from({ length: slotCount }, (_, i) => joinUrl(API_BASE, `/uploads/${name}_video_${i+1}.webm`));
    const gridImage = joinUrl(API_BASE, `/uploads/${name}_grid.jpg`);
    const videoGridImage = joinUrl(API_BASE, `/uploads/${name}_video_grid.webm`);
    
    // Photo Grid dan Video Grid selalu ada terpisah dari slot
    const imagesArray = [
      { type: 'grid', src: gridImage }, // Photo Grid (selalu ada)
      { type: 'video-grid', src: videoGridImage, videos: slotVideos }, // Video Grid (selalu ada)
      ...slotPhotos.map((slot, idx) => ({ 
        type: 'photo', 
        src: slot.jpg, // Default ke jpg
        fallbackSrc: slot.png, // Fallback ke png jika jpg tidak ada
        idx: slot.idx 
      })) // Foto per slot (sesuai jumlah grid yang dipilih)
    ];
    
    console.log('PhotoGallery: Final images array:', imagesArray);
    console.log('PhotoGallery: Total array count:', imagesArray.length, '(2 grid +', slotCount, 'slots)');
    setImages(imagesArray);
  }, [name, slotCount]);

  const goToSlide = idx => {
    // Pause semua video saat switching slides
    const videoElements = document.querySelectorAll('.result-photo video');
    videoElements.forEach(video => {
      video.pause();
    });
    setCurrentSlide(idx);
  };
  
  const goPrev = () => {
    // Pause semua video saat switching slides
    const videoElements = document.querySelectorAll('.result-photo video');
    videoElements.forEach(video => {
      video.pause();
    });
    setCurrentSlide(s => (s - 1 + images.length) % images.length);
  };
  
  const goNext = () => {
    // Pause semua video saat switching slides
    const videoElements = document.querySelectorAll('.result-photo video');
    videoElements.forEach(video => {
      video.pause();
    });
    setCurrentSlide(s => (s + 1) % images.length);
  };

  // Render grid ke canvas (mirip handleDownloadGridBlob di PhotoResult)
  async function renderGridPreview(photos, layout, gridCount) {
    // Buat elemen container sementara dengan styling yang sama dengan preview
    const container = document.createElement('div');
    container.className = `result-container ${getLayoutClass(layout, gridCount)}`;
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.background = '#fff';
    container.style.borderRadius = '8px';
    container.style.overflow = 'hidden';
    container.style.padding = '8px';
    container.style.transform = 'scaleX(-1)';
    container.style.display = 'grid';
    container.style.gap = '8px';
    container.style.width = '600px';
    container.style.height = '400px';
    document.body.appendChild(container);
    
    photos.forEach((photo, index) => {
      const div = document.createElement('div');
      div.className = 'result-photo';
      div.style.position = 'relative';
      div.style.width = '100%';
      div.style.height = '100%';
      div.style.overflow = 'hidden';
      div.style.borderRadius = '8px';
      div.style.background = '#000';
      
      const img = document.createElement('img');
      img.src = photo;
      img.alt = `Grid ${index + 1}`;
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'cover';
      img.style.borderRadius = '4px';
      img.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
      
      div.appendChild(img);
      container.appendChild(div);
    });
    
    await new Promise(r => setTimeout(r, 100)); // Tunggu render DOM
    const rect = container.getBoundingClientRect();
    const canvas = document.createElement('canvas');
    canvas.width = rect.width || 600;
    canvas.height = rect.height || 400;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    const photoElements = container.querySelectorAll('.result-photo img');
    const photoPromises = Array.from(photoElements).map(img => {
      return new Promise((resolve) => {
        const tempImg = new window.Image();
        tempImg.crossOrigin = 'anonymous';
        tempImg.onload = () => {
          const rect = img.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const x = (rect.left - containerRect.left) * (canvas.width / containerRect.width);
          const y = (rect.top - containerRect.top) * (canvas.height / containerRect.height);
          const width = rect.width * (canvas.width / containerRect.width);
          const height = rect.height * (canvas.height / containerRect.height);
          
          ctx.save();
          // Karena container sudah menggunakan scaleX(-1), kita tidak perlu mirror lagi
          ctx.drawImage(tempImg, x, y, width, height);
          ctx.restore();
          resolve();
        };
        tempImg.src = img.src;
      });
    });
    
    await Promise.all(photoPromises);
    setGridPreviewUrl(canvas.toDataURL('image/jpeg', 0.95));
    document.body.removeChild(container);
  }

  // Layout class for grid preview
  function getLayoutClass(layout, gridCount) {
    switch (gridCount) {
      case 1:
        return layout === 1 ? 'layout-single-centered' : 'layout-single-top';
      case 2:
        if (layout === 1) return 'layout-vertical-center';
        if (layout === 2) return 'layout-vertical-top';
        if (layout === 3) return 'layout-vertical-gap';
        return '';
      case 3:
        if (layout === 1) return 'layout-vertical-center-3';
        if (layout === 2) return 'layout-vertical-ends';
        return '';
      case 4:
        if (layout === 1) return 'layout-vertical-center-4';
        if (layout === 2) return 'layout-2-top-2-bottom';
        if (layout === 3) return 'layout-mix-center';
        return '';
      default:
        return '';
    }
  }

  useEffect(() => {
    // Manual GIF animation untuk video grid (slide index 1)
    if (images.length >= 2 && currentSlide === 1) {
      setManualGifIdx(0);
      const interval = setInterval(() => {
        setManualGifIdx(idx => (idx + 1) % images.length);
      }, manualGifInterval);
      return () => clearInterval(interval);
    }
  }, [currentSlide, images.length]);

  // Fungsi download file dari url/dataURL
  function downloadFile(url, filename) {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Fungsi download grid
  const handleDownloadGrid = () => {
    // Cek apakah ada photo grid yang sudah diupload
    const photoGridUrl = images[currentSlide].src;
    
    if (photoGridUrl) {
      // Jika ada photo grid yang sudah diupload, download langsung
      downloadFile(photoGridUrl, `photo-grid-${name}-${new Date().getTime()}.jpg`);
      return;
    }
    
    // Fallback: Jika tidak ada photo grid, gunakan gridPreviewUrl
    if (gridPreviewUrl) {
      downloadFile(gridPreviewUrl, `photo-grid-${name}-${new Date().getTime()}.jpg`);
    }
  };

  // Fungsi download GIF manual
  const handleDownloadGif = async () => {
    // Buat GIF dari finalPhotos (mirip renderGif, tapi return blob)
    const images = images.map(img => img.src);
    if (!window.GIF) {
      await new Promise(resolve => {
        const script = document.createElement('script');
        script.src = '/gif.js';
        script.onload = () => {
          console.log('gif.js loaded', window.GIF);
          resolve();
        };
        document.body.appendChild(script);
      });
    }
    if (typeof window.GIF !== 'function') {
      alert('GIF.js gagal di-load. Cek file /gif.js!');
      return;
    }
    return new Promise((resolve, reject) => {
      const gif = new window.GIF({ workers: 2, quality: 10 });
      images.forEach(src => {
        const img = document.createElement('img');
        img.src = src;
        gif.addFrame(img, { delay: manualGifInterval });
      });
      gif.on('finished', function(blob) {
        const url = URL.createObjectURL(blob);
        downloadFile(url, 'photo-slots.gif');
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        resolve();
      });
      gif.on('error', err => {
        alert('Gagal membuat GIF.');
        console.error('GIF render error:', err);
        reject(err);
      });
      gif.render();
    });
  };

  // Fungsi download foto per slot (mirror)
  function handleDownloadPhoto(idx) {
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.save();
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, 0, 0);
      ctx.restore();
      const url = canvas.toDataURL('image/jpeg', 0.95);
      downloadFile(url, `photo-slot-${idx + 1}-mirror.jpg`);
    };
    img.src = images[currentSlide].src;
  }

  // Fungsi download video per slot
  function handleDownloadVideo(idx) {
    const videoUrl = images[currentSlide].src;
    if (videoUrl) {
      const link = document.createElement('a');
      link.href = videoUrl;
      link.download = `video-slot-${idx + 1}.webm`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // Fungsi untuk render video grid
  const renderVideoGrid = (videos, layout, gridCount) => {
    const layoutClass = getLayoutClass(layout, gridCount);
    
    return (
      <div className={`result-container ${layoutClass}`} style={{
        background:'#fff',
        padding:8,
        transform:'scaleX(-1)',
        width: '100%',
        height: '100%'
      }}>
        {Array.from({ length: gridCount }).map((_, i) => (
          <div key={i} className="result-photo">
            {videos[i] ? (
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <video 
                  src={videos[i]} 
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  onLoadStart={(e) => {
                    // Loading state
                    e.target.style.opacity = '0.7';
                    const loadingIndicator = e.target.parentElement.querySelector('.loading-indicator');
                    if (loadingIndicator) loadingIndicator.style.display = 'flex';
                  }}
                  onCanPlay={(e) => {
                    // Video siap untuk play
                    e.target.style.opacity = '1';
                    const loadingIndicator = e.target.parentElement.querySelector('.loading-indicator');
                    if (loadingIndicator) loadingIndicator.style.display = 'none';
                    e.target.play().catch(err => console.log('Auto play failed:', err));
                  }}
                  onLoadedData={(e) => {
                    // Pastikan video mulai play setelah data loaded
                    e.target.style.opacity = '1';
                    const loadingIndicator = e.target.parentElement.querySelector('.loading-indicator');
                    if (loadingIndicator) loadingIndicator.style.display = 'none';
                    e.target.play().catch(err => console.log('Auto play failed:', err));
                  }}
                  onEnded={(e) => {
                    // Restart video saat selesai untuk smooth loop
                    e.target.currentTime = 0;
                    e.target.play().catch(err => console.log('Restart failed:', err));
                  }}
                  onError={(e) => {
                    console.error('Video error:', e);
                    // Hide loading indicator on error
                    const loadingIndicator = e.target.parentElement.querySelector('.loading-indicator');
                    if (loadingIndicator) loadingIndicator.style.display = 'none';
                    // Show error state
                    e.target.style.opacity = '0.5';
                  }}
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover',
                    transition: 'opacity 0.3s ease'
                  }}
                />
                {/* Loading indicator */}
                <div className="loading-indicator">
                  Loading...
                </div>
              </div>
            ) : (
              <div style={{ width: '100%', height: '100%', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                No Video
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Fungsi download video grid
  const handleDownloadVideoGrid = async () => {
    try {
      // Cek apakah ada video grid yang sudah diupload
      const videoGridUrl = images[currentSlide].src;
      
      if (videoGridUrl) {
        // Jika ada video grid yang sudah diupload, download langsung
        downloadFile(videoGridUrl, `video-grid-${name}-${new Date().getTime()}.webm`);
        return;
      }
      
      // Fallback: Jika tidak ada video grid, buat dari individual videos
      const currentVideos = images[currentSlide].videos;
      const validVideos = currentVideos.filter(v => v);
      
      if (validVideos.length === 0) {
        alert('Tidak ada video yang tersedia untuk diunduh.');
        return;
      }

      // Buat video grid gabungan menggunakan canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set ukuran canvas sesuai dengan preview container
      const previewContainer = document.querySelector('[style*="previewBoxStyle"]') || 
                              document.querySelector('[style*="width: clamp"]') ||
                              document.querySelector('[style*="90vw"]');
      
      let containerWidth = 600;
      let containerHeight = 400;
      
      if (previewContainer) {
        const rect = previewContainer.getBoundingClientRect();
        containerWidth = rect.width;
        containerHeight = rect.height;
      }
      
      canvas.width = containerWidth;
      canvas.height = containerHeight;
      
      // Buat container sementara yang sama persis dengan preview
      const tempContainer = document.createElement('div');
      tempContainer.className = `result-container ${getLayoutClass(selectedLayout, slotCount)}`;
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = `${containerWidth}px`;
      tempContainer.style.height = `${containerHeight}px`;
      tempContainer.style.background = '#fff';
      tempContainer.style.borderRadius = '8px';
      tempContainer.style.overflow = 'hidden';
      tempContainer.style.padding = '8px';
      tempContainer.style.transform = 'scaleX(-1)';
      // Gunakan CSS grid seperti di preview, bukan flex
      tempContainer.style.display = 'grid';
      tempContainer.style.gap = '8px';
      document.body.appendChild(tempContainer);
      
      const container = tempContainer;
      
      // Background putih
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Buat slot video di container sementara dengan styling yang sama
      for (let i = 0; i < slotCount; i++) {
        const slotDiv = document.createElement('div');
        slotDiv.className = 'result-photo';
        slotDiv.style.position = 'relative';
        slotDiv.style.width = '100%';
        slotDiv.style.height = '100%';
        slotDiv.style.overflow = 'hidden';
        slotDiv.style.borderRadius = '8px';
        slotDiv.style.background = '#000';
        container.appendChild(slotDiv);
      }

      // Buat video element untuk setiap slot dengan styling yang sama
      const videoElements = [];
      const videoPromises = validVideos.map((videoSrc, index) => {
        return new Promise((resolve) => {
          const video = document.createElement('video');
          video.src = videoSrc;
          video.muted = true;
          video.loop = true;
          video.playsInline = true;
          video.style.width = '100%';
          video.style.height = '100%';
          video.style.objectFit = 'cover';
          // Tidak perlu transform karena container sudah menggunakan scaleX(-1)
          video.style.transition = 'opacity 0.3s ease';
          video.style.borderRadius = '4px';
          video.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
          video.onloadedmetadata = () => {
            videoElements[index] = video;
            resolve();
          };
          video.onerror = () => resolve();
        });
      });

      await Promise.all(videoPromises);

      // Mulai recording canvas
      const stream = canvas.captureStream(30); // 30 FPS
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      const chunks = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `video-grid-${name}-${new Date().getTime()}.webm`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        alert('Video grid berhasil diunduh!');
      };

      // Mulai recording
      mediaRecorder.start();

      // Render video grid ke canvas
      const renderFrame = () => {
        // Clear canvas
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Render setiap video sesuai layout
        videoElements.forEach((video, index) => {
          if (video && video.readyState >= 2) {
            const slotDiv = container.children[index];
            if (slotDiv) {
              const rect = slotDiv.getBoundingClientRect();
              const containerRect = container.getBoundingClientRect();
              
              const x = (rect.left - containerRect.left) * (canvas.width / containerRect.width);
              const y = (rect.top - containerRect.top) * (canvas.height / containerRect.height);
              const width = rect.width * (canvas.width / containerRect.width);
              const height = rect.height * (canvas.height / containerRect.height);
              
              ctx.save();
              // Karena container sudah menggunakan scaleX(-1), kita tidak perlu mirror lagi
              ctx.drawImage(video, x, y, width, height);
              ctx.restore();
            }
          }
        });

        if (mediaRecorder.state === 'recording') {
          requestAnimationFrame(renderFrame);
        }
      };

      // Mulai render dan play semua video
      videoElements.forEach(video => {
        if (video) {
          video.play();
        }
      });

      renderFrame();

      // Stop recording setelah 5 detik
      setTimeout(() => {
        mediaRecorder.stop();
        videoElements.forEach(video => {
          if (video) {
            video.pause();
            video.src = '';
          }
        });
        // Cleanup container sementara
        if (tempContainer.parentNode) {
          tempContainer.parentNode.removeChild(tempContainer);
        }
      }, 5000);

    } catch (error) {
      console.error('Error downloading video grid:', error);
      alert('Gagal mengunduh video grid. Silakan coba lagi.');
    }
  };

  // Responsive style for preview box and nav buttons
  const isMobileSmall = typeof window !== 'undefined' && window.innerWidth < 500;
  const previewBoxStyle = {
    position:'relative',
    width: isMobileSmall ? '98vw' : 'clamp(180px, 90vw, 420px)',
    height: isMobileSmall ? '60vw' : 'clamp(180px, 60vw, 420px)',
    minWidth: isMobileSmall ? 120 : 180,
    minHeight: isMobileSmall ? 120 : 180,
    display:'flex',
    alignItems:'center',
    justifyContent:'center',
    background:'#eee',
    borderRadius:8,
    marginBottom:24,
    marginLeft:'auto',
    marginRight:'auto',
    boxShadow:'0 2px 16px rgba(0,0,0,0.07)',
    overflow:'visible',
  };
  const navBtnStyle = side => ({
    position:'absolute',
    [side]: isMobileSmall ? 8 : 'max(-56px, -12vw)',
    top:'50%',
    transform:'translateY(-50%)',
    zIndex:2,
    width: isMobileSmall ? 36 : 48,
    height: isMobileSmall ? 36 : 48,
    borderRadius:'50%',
    background:'rgba(0,0,0,0.25)',
    border:'none',
    color:'#fff',
    fontSize: isMobileSmall ? 22 : 32,
    boxShadow:'0 2px 8px rgba(0,0,0,0.15)',
    cursor:'pointer',
    display:'flex',alignItems:'center',justifyContent:'center',
    transition:'background 0.2s',
    outline:'none',
  });

  useEffect(() => {
    // Render grid preview hanya jika slide grid aktif dan slot images sudah siap
    if (images.length > 2 && images[0].type === 'grid' && currentSlide === 0) {
      const slotImages = images.slice(2).map(img => img.src);
      renderGridPreview(slotImages, selectedLayout, slotCount);
    }
    // eslint-disable-next-line
  }, [currentSlide, images, selectedLayout, slotCount]);

  // Handle video grid display untuk smooth autoplay di gallery
  useEffect(() => {
    if (images.length > 0 && images[currentSlide] && images[currentSlide].type === 'video-grid') {
      // Delay kecil untuk memastikan DOM sudah ter-render
      const timer = setTimeout(() => {
        const videoElements = document.querySelectorAll('.result-photo video');
        let loadedVideos = 0;
        const totalVideos = videoElements.length;
        
        // Fungsi untuk memulai semua video setelah semua loaded
        const startAllVideos = () => {
          videoElements.forEach((video, index) => {
            if (video) {
              // Set video properties untuk smooth playback
              video.currentTime = 0;
              video.playbackRate = 1.0;
              video.volume = 0; // Muted
              
              // Optimize video performance
              video.style.willChange = 'transform, opacity';
              
              // Tambahkan event listener untuk smooth loop
              const loopHandler = () => {
                video.currentTime = 0;
                video.play().catch(err => console.log(`Gallery video ${index + 1} loop failed:`, err));
              };
              
              // Store reference untuk cleanup
              video.loopHandler = loopHandler;
              video.addEventListener('ended', loopHandler);
              
              // Play video dengan error handling
              video.play().catch(err => {
                console.log(`Gallery video ${index + 1} auto play failed:`, err);
                // Retry play setelah delay
                setTimeout(() => {
                  video.play().catch(retryErr => console.log(`Gallery video ${index + 1} retry failed:`, retryErr));
                }, 1000);
              });
            }
          });
        };

        // Track loading progress
        videoElements.forEach((video, index) => {
          if (video) {
            const onLoaded = () => {
              loadedVideos++;
              if (loadedVideos === totalVideos) {
                // Semua video sudah loaded, mulai play
                startAllVideos();
              }
            };
            
            if (video.readyState >= 2) {
              // Video sudah loaded
              onLoaded();
            } else {
              // Tunggu video load
              video.addEventListener('loadeddata', onLoaded, { once: true });
            }
          }
        });
      }, 500);

      return () => {
        clearTimeout(timer);
        // Cleanup video event listeners dan pause videos
        const videoElements = document.querySelectorAll('.result-photo video');
        videoElements.forEach(video => {
          if (video.loopHandler) {
            video.removeEventListener('ended', video.loopHandler);
            video.loopHandler = null;
          }
          // Pause video saat cleanup
          if (!video.paused) {
            video.pause();
          }
        });
      };
    }
  }, [currentSlide, images]);

  // Cleanup saat komponen unmount
  useEffect(() => {
    return () => {
      // Cleanup semua video saat komponen unmount
      const videoElements = document.querySelectorAll('.result-photo video');
      videoElements.forEach(video => {
        if (video.loopHandler) {
          video.removeEventListener('ended', video.loopHandler);
          video.loopHandler = null;
        }
        if (!video.paused) {
          video.pause();
        }
      });
    };
  }, []);

  return (
    <div style={{
      minHeight:'100vh',
      width:'100vw',
      display:'flex',
      flexDirection:'column',
      alignItems:'center',
      justifyContent:'center',
      background:'#fafbfc',
      boxSizing:'border-box',
      padding:'24px 0',
      position:'relative',
    }}>
      <h2>Gallery: {name}</h2>
      {images.length > 0 && (
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',marginBottom:24}}>
          <div style={previewBoxStyle}>
            <button
              onClick={goPrev}
              style={navBtnStyle('left')}
              aria-label="Sebelumnya"
            >
              <svg width={isMobileSmall ? 24 : 36} height={isMobileSmall ? 24 : 36} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={isMobileSmall ? 2.2 : 3.2} strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            {images[currentSlide].type === 'grid' ? (
              <div style={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',background:'#fff',borderRadius:8,overflow:'hidden',transform:'scaleX(-1)'}}>
                <img src={images[currentSlide].src} alt="Grid" style={{maxWidth:'100%',maxHeight:'100%',borderRadius:8,display:'block'}} onError={e => e.currentTarget.src = ''} />
                {!images[currentSlide].src && <span>File not found</span>}
              </div>
            ) : images[currentSlide].type === 'video-grid' ? (
              <div style={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',background:'#fff',borderRadius:8,overflow:'hidden'}}>
                {images[currentSlide].src ? (
                  <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <video 
                      src={images[currentSlide].src} 
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="auto"
                      onLoadStart={(e) => {
                        // Loading state
                        e.target.style.opacity = '0.7';
                        const loadingIndicator = e.target.parentElement.querySelector('.loading-indicator');
                        if (loadingIndicator) loadingIndicator.style.display = 'flex';
                      }}
                      onCanPlay={(e) => {
                        // Video siap untuk play
                        e.target.style.opacity = '1';
                        const loadingIndicator = e.target.parentElement.querySelector('.loading-indicator');
                        if (loadingIndicator) loadingIndicator.style.display = 'none';
                        e.target.play().catch(err => console.log('Gallery video auto play failed:', err));
                      }}
                      onLoadedData={(e) => {
                        // Pastikan video mulai play setelah data loaded
                        e.target.style.opacity = '1';
                        const loadingIndicator = e.target.parentElement.querySelector('.loading-indicator');
                        if (loadingIndicator) loadingIndicator.style.display = 'none';
                        e.target.play().catch(err => console.log('Gallery video auto play failed:', err));
                      }}
                      onEnded={(e) => {
                        // Restart video saat selesai untuk smooth loop
                        e.target.currentTime = 0;
                        e.target.play().catch(err => console.log('Gallery video restart failed:', err));
                      }}
                      onError={(e) => {
                        console.log('Video grid not found, falling back to individual videos');
                        e.currentTarget.style.display = 'none';
                        // Hide loading indicator on error
                        const loadingIndicator = e.target.parentElement.querySelector('.loading-indicator');
                        if (loadingIndicator) loadingIndicator.style.display = 'none';
                        // Fallback ke render video grid individual
                        const fallbackContainer = e.target.parentElement;
                        fallbackContainer.innerHTML = '';
                        const videoGridElement = renderVideoGrid(images[currentSlide].videos, selectedLayout, slotCount);
                        fallbackContainer.appendChild(videoGridElement);
                      }}
                      style={{
                        maxWidth:'100%',
                        maxHeight:'100%',
                        borderRadius:8,
                        transition: 'opacity 0.3s ease'
                      }}
                    />
                    {/* Loading indicator */}
                    <div className="loading-indicator">
                      Loading...
                    </div>
                  </div>
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {renderVideoGrid(images[currentSlide].videos, selectedLayout, slotCount)}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <img 
                src={images[currentSlide].src} 
                alt={`Slot ${images[currentSlide].idx+1}`} 
                style={{maxWidth:'100%',maxHeight:'100%',borderRadius:8,transform:'scaleX(-1)'}} 
                onError={(e) => {
                  console.log(`PhotoGallery: Image error for slot ${images[currentSlide].idx+1}:`, e.currentTarget.src);
                  // Coba fallback ke PNG jika JPG tidak ada
                  if (images[currentSlide].fallbackSrc && e.currentTarget.src === images[currentSlide].src) {
                    console.log(`PhotoGallery: Trying fallback PNG for slot ${images[currentSlide].idx+1}:`, images[currentSlide].fallbackSrc);
                    e.currentTarget.src = images[currentSlide].fallbackSrc;
                  } else {
                    console.log(`PhotoGallery: Both JPG and PNG failed for slot ${images[currentSlide].idx+1}`);
                    // Jika kedua file tidak ada, tampilkan placeholder
                    e.currentTarget.style.display = 'none';
                    const placeholder = document.createElement('div');
                    placeholder.style.cssText = `
                      width: 100%;
                      height: 100%;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      background: #f0f0f0;
                      color: #666;
                      font-size: 16px;
                      border-radius: 8px;
                      transform: scaleX(-1);
                    `;
                    placeholder.textContent = `Slot ${images[currentSlide].idx+1} - File tidak ditemukan`;
                    e.currentTarget.parentNode.appendChild(placeholder);
                  }
                }} 
              />
            )}
            <button
              onClick={goNext}
              style={navBtnStyle('right')}
              aria-label="Berikutnya"
            >
              <svg width={isMobileSmall ? 24 : 36} height={isMobileSmall ? 24 : 36} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={isMobileSmall ? 2.2 : 3.2} strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
          <div style={{display:'flex',gap:8,marginTop:16,flexWrap:'wrap',justifyContent:'center'}}>
            {images.map((img, idx) => (
              <div key={idx} style={{width:60,height:60,background:'#fff',display:'flex',alignItems:'center',justifyContent:'center',borderRadius:4,border:currentSlide===idx?'2px solid #007bff':'2px solid #ccc',cursor:'pointer',overflow:'hidden'}} onClick={() => goToSlide(idx)}>
                {img.type === 'grid' ? (
                  <img src={img.src} alt="Grid Thumb" style={{width:60,height:60,objectFit:'cover',borderRadius:4,transform:'scaleX(-1)'}} onError={e => e.currentTarget.style.display = 'none'} />
                ) : img.type === 'video-grid' ? (
                  <div style={{width:60,height:60,display:'flex',alignItems:'center',justifyContent:'center',background:'#007bff',color:'white',fontSize:12}}>
                    Video Grid
                  </div>
                ) : (
                  <img 
                    src={img.src} 
                    alt="Photo Thumb" 
                    style={{width:60,height:60,objectFit:'cover',borderRadius:4,transform:'scaleX(-1)'}} 
                    onError={(e) => {
                      // Coba fallback ke PNG jika JPG tidak ada
                      if (img.fallbackSrc && e.currentTarget.src === img.src) {
                        e.currentTarget.src = img.fallbackSrc;
                      } else {
                        // Jika kedua file tidak ada, tampilkan placeholder
                        e.currentTarget.style.display = 'none';
                        const placeholder = document.createElement('div');
                        placeholder.style.cssText = `
                          width: 60px;
                          height: 60px;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          background: #f0f0f0;
                          color: #666;
                          font-size: 10px;
                          border-radius: 4px;
                          text-align: center;
                          line-height: 1.2;
                        `;
                        placeholder.textContent = `Slot ${img.idx+1}`;
                        e.currentTarget.parentNode.appendChild(placeholder);
                      }
                    }} 
                  />
                )}
              </div>
            ))}
          </div>
          <div style={{margin:'16px 0',textAlign:'center'}}>
            <button
              onClick={async () => {
                if (images[currentSlide].type === 'grid') handleDownloadGrid();
                else if (images[currentSlide].type === 'video-grid') handleDownloadVideoGrid();
                else if (images[currentSlide].type === 'photo') handleDownloadPhoto(images[currentSlide].idx);
              }}
              style={{
                padding:0,
                width:48,
                height:48,
                borderRadius:'50%',
                background:'#007bff',
                color:'#fff',
                border:'none',
                cursor:'pointer',
                display:'flex',alignItems:'center',justifyContent:'center',
                boxShadow:'0 2px 8px rgba(0,0,0,0.15)',
                transition:'background 0.2s',
                outline:'none',
                fontSize:0
              }}
              title="Download"
              onMouseOver={e => e.currentTarget.style.background='#0056b3'}
              onMouseOut={e => e.currentTarget.style.background='#007bff'}
            >
              {/* SVG download icon */}
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            </button>
          </div>
        </div>
      )}
      {showVideoPreview && videoPreviewUrl && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: '#000a', zIndex: 1000, display: 'flex',
          flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', padding: 24, borderRadius: 8,
            maxWidth: 600, width: '90vw', maxHeight: '100vh', overflowY: 'auto', textAlign: 'center'
          }}>
            <h3>Preview Video</h3>
            <video src={videoPreviewUrl} controls autoPlay loop style={{ maxWidth: '100%', borderRadius: 8 }} />
            <div>
              <button
                onClick={() => {
                  downloadFile(videoPreviewUrl, 'photo-slots.mp4');
                  setShowVideoPreview(false);
                  setTimeout(() => URL.revokeObjectURL(videoPreviewUrl), 2000);
                }}
                style={{ marginTop: 16, padding: '8px 24px', fontSize: 16, borderRadius: 6, background: '#007bff', color: '#fff', border: 'none', cursor: 'pointer' }}
              >
                Download Video
              </button>
              <button
                onClick={() => {
                  setShowVideoPreview(false);
                  setTimeout(() => URL.revokeObjectURL(videoPreviewUrl), 2000);
                }}
                style={{ marginTop: 16, marginLeft: 8, padding: '8px 24px', fontSize: 16, borderRadius: 6, background: '#ccc', color: '#333', border: 'none', cursor: 'pointer' }}
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoGallery; 